import React, { useState, useEffect, useRef } from 'react'
import CryptoJS from 'crypto-js';
import './DomainCheck.scss';

/**
 * domainObj 类型
 * 
 * @typedef {Object} DomainItem
 * @property {string} key - 域名标识
 * @property {string} domain - 域名
 * @property {string} status - 域名状态: pending 待检测, success 检测成功, failed 检测失败
 * @property {number} responseTime - 响应时间(ms)
 * @property {string} lastCheckTime - 最后检查时间
 */

const DomainCheck = () => {
  const [domainObj, setDomainObj] = useState({});
  const [isChecking, setIsChecking] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const isFirstUpdate = useRef(true);

  const createSign = () => {
    const key = 'seektop-qchat';
    const timestamp = Date.parse(new Date());
    const deviceId = '1234567890';
    const deviceType = '1';
    const version = '1.0.0';
    
    //拼接
    const strSign = `deviceId=${deviceId}&deviceType=${deviceType}&timestamp=${timestamp}&version=${version}${key}`;
    const sign = CryptoJS.MD5(strSign).toString();

    return {
      deviceId,
      deviceType,
      timestamp,
      version,
      sign,
    };
  }

  const fetchDomainData = async () => {
    const { deviceId, deviceType, timestamp, version, sign } = createSign();
    
    try {
      const res = await fetch('/api/forehead/system/domain/list', {
        method: 'POST',
        headers: {
          "deviceId": deviceId,
          "deviceType": deviceType,
          "timestamp": timestamp,
          "version": version,
          "sign": sign
        },
      })
      const data = await res.json();
      
      if (data.code !== 1) {
        throw new Error(data.message);
      }

      return data.data;
    } catch (error) {
      alert(error.message);
      return {};
    }
  }

  const domainDataToDomainList = (domainObj) => {
    let domainList = [];
    for (const key in domainObj) {
      domainList = domainList.concat(domainObj[key]);
    }
    return domainList;
  }

  const initialDomainList = async () => {
    const data = await fetchDomainData();
    const list = domainDataToDomainList(data);
    const obj = list.reduce((acc, item) => {
      acc[item] = {
        key: item,
        domain: item,
        status: 'pending',
        responseTime: 0,
        lastCheckTime: null
      };
      return acc;
    }, {});

    setDomainObj(obj);
  }

  const checkDomain = (domain) => {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const xhr = new XMLHttpRequest();
      xhr.open('GET', domain, true);
      xhr.timeout = 5000; // 5秒超时
      
      xhr.onload = function() {
        const endTime = Date.now();
        resolve({
          success: xhr.status >= 200 && xhr.status < 300,
          responseTime: endTime - startTime
        });
      };
      
      xhr.onerror = function() {
        resolve({
          success: false,
          responseTime: Date.now() - startTime
        });
      };
      
      xhr.ontimeout = function() {
        resolve({
          success: false,
          responseTime: 5000
        });
      };
      
      xhr.send();
    });
  }

  const checkAllDomains = async () => {
    setIsChecking(true);
    const domains = Object.values(domainObj);
    const now = new Date().toISOString();

    for (const domain of domains) {
      const result = await checkDomain(domain.domain);
      setDomainObj(prev => ({
        ...prev,
        [domain.key]: {
          ...domain,
          status: result.success ? 'success' : 'failed',
          responseTime: result.responseTime,
          lastCheckTime: now
        }
      }));
    }

    setLastUpdateTime(now);
    setIsChecking(false);
  }

  useEffect(() => {
    const initialPage = async () => {
      await initialDomainList();
    }

    initialPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 只在 domainObj 初次更新时执行 checkAllDomains
  useEffect(() => {
    if (isFirstUpdate.current && Object.keys(domainObj).length > 0) {
      checkAllDomains();
      isFirstUpdate.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainObj]);

  const getStatusText = (status) => {
    switch (status) {
      case 'success':
        return '正常';
      case 'failed':
        return '异常';
      default:
        return '待检测';
    }
  }

  return (
    <div className="domain-check">
      <div className="domain-check__header">
        <h1 className="domain-check__title">域名状态监测</h1>
        <div className="domain-check__controls">
          {lastUpdateTime && (
            <span className="domain-check__update-time">
              最后更新: {new Date(lastUpdateTime).toLocaleString()}
            </span>
          )}
          <button
            onClick={() => {
              isFirstUpdate.current = true;
              initialDomainList();
            }}
            disabled={isChecking}
            className="domain-check__button"
          >
            刷新域名
          </button>
          <button
            onClick={checkAllDomains}
            disabled={isChecking}
            className="domain-check__button"
          >
            {isChecking ? '检测中...' : '重新检测'}
          </button>
        </div>
      </div>

      <div className="domain-check__table-container">
        <table className="domain-check__table">
          <thead>
            <tr>
              <th>域名</th>
              <th>状态</th>
              <th>响应时间</th>
              <th>最后检查时间</th>
            </tr>
          </thead>
          <tbody>
            {Object.values(domainObj).map((item) => (
              <tr key={item.key}>
                <td>{item.domain}</td>
                <td>
                  <span className={`domain-check__status domain-check__status--${item.status}`}>
                    {getStatusText(item.status)}
                  </span>
                </td>
                <td>{item.responseTime ? `${item.responseTime}ms` : '-'}</td>
                <td>{item.lastCheckTime ? new Date(item.lastCheckTime).toLocaleString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default DomainCheck;